import { html, property } from 'lit-element';
import { KatLitElement, register } from '../../shared/base';

import baseStyles from '../../shared/base/base.lit.scss';
import styles from './flag.lit.scss';

/**
 * @component {kat-flag} KatalFlag Flags are a subset of icons that represent various countries and regions of the world.
 * @guideline Do Use the correct size. The default is small.
 * @guideline Do Use flags with caution. Some may present localization issues.
 * @guideline Dont Don't use a flag as shorthand for a language selection. Some countries have multiple languages.
 * @example Small-USA-Flag {"country": "us", "size":"small"}
 * @example Large-USA-Flag {"country": "us", "size":"large"}
 * @example Large-Colombia-Flag {"country": "co", "size":"large"}
 * @status Production
 * @theme flo
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-flag')
export class KatFlag extends KatLitElement {
  /**
   * The size of the flag - Default is small
   * @enum {value} tiny Tiny
   * @enum {value} small Small - Default
   * @enum {value} large Large
   */
  @property()
  size?: string;

  /**
   * The two letter ISO code of the country - Default is "us"
   * @enum {value} ae United Arab Emirates
   * @enum {value} al Albania
   * @enum {value} am Armenia
   * @enum {value} at Austria
   * @enum {value} au Australia
   * @enum {value} ba Bosnia and Herzegovina
   * @enum {value} be Belgium
   * @enum {value} bg Bulgaria
   * @enum {value} bh Bahrain
   * @enum {value} br Brazil
   * @enum {value} by Belarus
   * @enum {value} ca Canada
   * @enum {value} ch Switzerland
   * @enum {value} cl Chile
   * @enum {value} cn China
   * @enum {value} co Colombia
   * @enum {value} cy Cyprus
   * @enum {value} cz Czech Republic
   * @enum {value} de Germany
   * @enum {value} dk Denmark
   * @enum {value} do Dominican Republic
   * @enum {value} ee Estonia
   * @enum {value} eg Egypt
   * @enum {value} es Spain
   * @enum {value} eu European Union
   * @enum {value} fi Finland
   * @enum {value} fr France
   * @enum {value} gb Great Britain
   * @enum {value} ge Georgia
   * @enum {value} gr Greece
   * @enum {value} hk Hong Kong
   * @enum {value} hr Croatia
   * @enum {value} hu Hungary
   * @enum {value} id Indonesia
   * @enum {value} ie Ireland
   * @enum {value} il Israel
   * @enum {value} in India
   * @enum {value} is Iceland
   * @enum {value} it Italy
   * @enum {value} jo Jordan
   * @enum {value} jp Japan
   * @enum {value} kp North Korea
   * @enum {value} kr South Korea
   * @enum {value} kw Kuwait
   * @enum {value} lb Lebanon
   * @enum {value} li Liechtenstein
   * @enum {value} lt Lithuania
   * @enum {value} lu Luxembourg
   * @enum {value} lv Latvia
   * @enum {value} ma Morocco
   * @enum {value} mc Monaco
   * @enum {value} md Moldova
   * @enum {value} me Montenegro
   * @enum {value} mk Macedonia
   * @enum {value} mx Mexico
   * @enum {value} my Malaysia
   * @enum {value} mt Malta
   * @enum {value} ng Nigeria
   * @enum {value} nl Netherlands
   * @enum {value} no Norway
   * @enum {value} nz New Zealand
   * @enum {value} om Oman
   * @enum {value} ph Philippines
   * @enum {value} pl Poland
   * @enum {value} pt Portugal
   * @enum {value} qa Qatar
   * @enum {value} ro Romania
   * @enum {value} rs Serbia
   * @enum {value} ru Russia
   * @enum {value} sa Saudi Arabia
   * @enum {value} se Sweden
   * @enum {value} sg Singapore
   * @enum {value} si Slovenia
   * @enum {value} sk Slovakia
   * @enum {value} sm San Marino
   * @enum {value} th Thailand
   * @enum {value} tr Turkey
   * @enum {value} ua Ukraine
   * @enum {value} uk United Kingdom
   * @enum {value} us United States
   * @enum {value} vn Vietnam
   * @enum {value} xk Kosovo
   * @enum {value} za South Africa
   */
  @property()
  country: string;

  static get styles() {
    return [baseStyles, styles];
  }

  render() {
    const name = this.country ? this.country.toLowerCase() : 'us';
    const size = this.size || 'small';
    return html`
      <kat-icon class="icon" size=${size} name=${`flag-${name}`}></kat-icon>
    `;
  }
}
